'use client'

import {
    ProviderAccountFees,
    getProviderAccountFees,
} from '@/lib/api/financial/payment-account-api'
import { useCallback, useEffect, useState } from 'react'
import {
    MdAccountBalance,
    MdCreditCard,
    MdInfoOutline,
    MdPix,
    MdRefresh,
    MdSwapHoriz,
    MdTrendingUp,
} from 'react-icons/md'
import { toast } from 'react-toastify'

/**
 * Taxas FIXAS: o que a operadora de pagamento cobra NA CONTA DESTA EMPRESA.
 *
 * O nome do provedor não aparece na tela: para o operador, é a taxa da operadora —
 * trocar de gateway não pode obrigar a reescrever a interface.
 *
 * Somente leitura, lida do provedor (`GET /myAccount/fees`) com a chave da própria
 * subconta. Não é campo de digitação de propósito: tabela copiada à mão envelhece em
 * silêncio, e a margem passa a mentir sem ninguém perceber. A empresa usa esta aba
 * para decidir, na aba "Taxas", o que repassar ao cliente.
 */

const formatCents = (cents: number): string =>
    `R$ ${(cents / 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`

const formatPercent = (value: number): string =>
    `${value.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}%`

/** "2,69% + R$ 0,29" — o formato em que o provedor cobra e o operador raciocina. */
const formatCombined = (percent: number, fixedCents: number): string => {
    if (percent > 0 && fixedCents > 0)
        return `${formatPercent(percent)} + ${formatCents(fixedCents)}`
    if (percent > 0) return formatPercent(percent)
    return formatCents(fixedCents)
}

const formatDays = (days: number | null): string =>
    days == null ? '—' : days === 1 ? '1 dia' : `${days} dias`

function FeeRow({
    label,
    value,
    hint,
}: {
    label: string
    value: string
    hint?: string
}) {
    return (
        <div className="flex items-start justify-between gap-4 border-b border-base-200 py-2 last:border-0">
            <div>
                <span className="text-sm">{label}</span>
                {hint && <p className="text-xs text-base-content/50">{hint}</p>}
            </div>
            <span className="whitespace-nowrap text-sm font-semibold tabular-nums">
                {value}
            </span>
        </div>
    )
}

function FeeCard({
    title,
    icon,
    children,
    footer,
}: {
    title: string
    icon: React.ReactNode
    children: React.ReactNode
    footer?: string
}) {
    return (
        <div className="card bg-base-100 shadow-sm border border-base-300">
            <div className="card-body">
                <div className="flex items-center gap-2 mb-2">
                    {icon}
                    <h2 className="card-title text-lg">{title}</h2>
                </div>
                <div>{children}</div>
                {footer && (
                    <p className="mt-3 text-xs text-base-content/50">
                        {footer}
                    </p>
                )}
            </div>
        </div>
    )
}

export default function ProviderFeesTab() {
    const [fees, setFees] = useState<ProviderAccountFees | null>(null)
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const load = useCallback(async (force = false) => {
        if (force) setRefreshing(true)
        else setLoading(true)
        try {
            const response = await getProviderAccountFees(force)
            if (response.success && response.result) {
                setFees(response.result)
                setError(null)
                if (force) toast.success('Taxas atualizadas')
            } else {
                setError(
                    response.error ||
                        'Não foi possível ler as taxas fixas desta conta.'
                )
            }
        } catch {
            setError('Não foi possível ler as taxas fixas desta conta.')
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }, [])

    useEffect(() => {
        void load()
    }, [load])

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <span className="loading loading-spinner loading-lg text-secondary" />
            </div>
        )
    }

    if (error || !fees) {
        return (
            <div className="alert alert-warning">
                <MdInfoOutline className="text-xl" />
                <div>
                    <p className="font-semibold">
                        {error || 'Taxas indisponíveis'}
                    </p>
                    <p className="text-sm">
                        A conta digital precisa estar ativa para consultar as
                        taxas praticadas.
                    </p>
                </div>
                <button
                    className="btn btn-sm"
                    onClick={() => void load(true)}
                    disabled={refreshing}
                >
                    Tentar de novo
                </button>
            </div>
        )
    }

    const { payment, transfer, anticipation } = fees
    const pixCreditsLeft = Math.max(
        0,
        payment.pix.monthlyCreditsWithoutFee -
            payment.pix.creditsReceivedOfCurrentMonth
    )

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-base-300 bg-base-200/50 px-4 py-3">
                <div className="flex items-start gap-2">
                    <MdInfoOutline className="mt-0.5 text-lg text-base-content/60" />
                    <div>
                        <p className="text-sm">
                            Taxas fixas que a <strong>operadora</strong> cobra
                            na conta desta empresa. Servem de base para decidir,
                            na aba{' '}
                            <strong>Taxas</strong>, o que é repassado ao
                            cliente.
                        </p>
                        <p className="text-xs text-base-content/50">
                            Última leitura em{' '}
                            {new Date(fees.fetchedAt).toLocaleString('pt-BR')}
                        </p>
                    </div>
                </div>
                <button
                    className="btn btn-sm btn-outline gap-2"
                    onClick={() => void load(true)}
                    disabled={refreshing}
                >
                    {refreshing ? (
                        <span className="loading loading-spinner loading-xs" />
                    ) : (
                        <MdRefresh className="text-base" />
                    )}
                    Atualizar
                </button>
            </div>

            <FeeCard
                title="Cartão de crédito"
                icon={<MdCreditCard className="text-xl text-secondary" />}
                footer={`Recebimento em ${formatDays(payment.creditCard.daysToReceive)} após o pagamento.`}
            >
                <FeeRow
                    label="À vista"
                    value={formatCombined(
                        payment.creditCard.oneInstallmentPercent,
                        payment.creditCard.operationValueCents
                    )}
                />
                <FeeRow
                    label="2 a 6 parcelas"
                    value={formatCombined(
                        payment.creditCard.upToSixInstallmentsPercent,
                        payment.creditCard.operationValueCents
                    )}
                />
                <FeeRow
                    label="7 a 12 parcelas"
                    value={formatCombined(
                        payment.creditCard.upToTwelveInstallmentsPercent,
                        payment.creditCard.operationValueCents
                    )}
                />
                <FeeRow
                    label="13 a 21 parcelas"
                    value={formatCombined(
                        payment.creditCard.upToTwentyOneInstallmentsPercent,
                        payment.creditCard.operationValueCents
                    )}
                />
            </FeeCard>

            <FeeCard
                title="Cartão de débito"
                icon={<MdCreditCard className="text-xl text-secondary" />}
                footer={`Recebimento em ${formatDays(payment.debitCard.daysToReceive)} após o pagamento.`}
            >
                <FeeRow
                    label="Por transação"
                    value={formatCombined(
                        payment.debitCard.percent,
                        payment.debitCard.operationValueCents
                    )}
                />
            </FeeCard>

            <FeeCard
                title="Pix e boleto"
                icon={<MdPix className="text-xl text-secondary" />}
                footer={`Boleto: recebimento em ${formatDays(payment.bankSlip.daysToReceive)} após o pagamento.`}
            >
                <FeeRow
                    label="Pix recebido"
                    value={formatCombined(
                        payment.pix.percent,
                        payment.pix.fixedFeeCents
                    )}
                    hint={
                        payment.pix.maximumFeeCents > 0
                            ? `Mínimo ${formatCents(payment.pix.minimumFeeCents)}, máximo ${formatCents(payment.pix.maximumFeeCents)}`
                            : undefined
                    }
                />
                {payment.pix.monthlyCreditsWithoutFee > 0 && (
                    <FeeRow
                        label="Recebimentos Pix sem taxa no mês"
                        value={`${pixCreditsLeft} de ${payment.pix.monthlyCreditsWithoutFee}`}
                        hint={`${payment.pix.creditsReceivedOfCurrentMonth} já usados neste mês`}
                    />
                )}
                <FeeRow
                    label="Boleto pago"
                    value={formatCents(payment.bankSlip.valueCents)}
                />
            </FeeCard>

            <FeeCard
                title="Transferências (repasses)"
                icon={<MdSwapHoriz className="text-xl text-secondary" />}
                footer="É o custo de cada repasse pago a parceiro, profissional ou hospital. Como o repasse agrupado paga um destinatário de uma vez, o custo é por destinatário — não por atendimento."
            >
                <FeeRow
                    label="Pix enviado"
                    value={formatCents(transfer.pix.feeCents)}
                />
                <FeeRow
                    label="TED enviada"
                    value={formatCents(transfer.ted.feeCents)}
                />
                {transfer.monthlyTransfersWithoutFee > 0 && (
                    <FeeRow
                        label="Transferências sem taxa no mês"
                        value={String(transfer.monthlyTransfersWithoutFee)}
                        hint="Vale para transferência entre contas do mesmo CNPJ. Repasse a terceiro é cobrado desde o primeiro."
                    />
                )}
            </FeeCard>

            <FeeCard
                title="Antecipação"
                icon={<MdTrendingUp className="text-xl text-secondary" />}
                footer="Taxas mensais. Só incidem quando a empresa antecipa o recebimento."
            >
                <FeeRow
                    label="Cartão à vista"
                    value={`${formatPercent(anticipation.creditCardDetachedMonthlyPercent)} ao mês`}
                />
                <FeeRow
                    label="Cartão parcelado"
                    value={`${formatPercent(anticipation.creditCardInstallmentMonthlyPercent)} ao mês`}
                />
                <FeeRow
                    label="Boleto e Pix"
                    value={`${formatPercent(anticipation.bankSlipMonthlyPercent)} ao mês`}
                />
            </FeeCard>

            <p className="flex items-start gap-2 text-xs text-base-content/50">
                <MdAccountBalance className="mt-0.5 shrink-0 text-sm" />
                Valores informados pela operadora para esta conta. Se a sua tabela
                foi renegociada e a tela ainda mostra a anterior, use Atualizar.
            </p>
        </div>
    )
}
